/**
 * Centrale analytics-helper. Alle marketplace-events worden hier naar de
 * GTM dataLayer gepusht — pixel-agnostisch. Meta, TikTok en GA4 krijgen elk
 * event via GTM-tags; in de app-code roepen we nooit fbq/ttq/gtag rechtstreeks.
 *
 * Eventnamen volgen de Tracking & Analytics Briefing (GA4 e-commerce-structuur).
 */

type DataLayerObject = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: DataLayerObject[];
  }
}

const CURRENCY = "EUR";

// ── User-context ─────────────────────────────────────────────────────────────
// Cumulatieve user_type op basis van de volledige gebruikersgeschiedenis:
//   guest        — niet ingelogd
//   new          — ingelogd, nog nooit geboden of een listing gemaakt
//   buyer        — ooit geboden, nooit een listing gemaakt
//   seller       — ooit een listing gemaakt, nooit geboden
//   buyer_seller — beide ooit gedaan
// AuthContext haalt de historie op (GET /users/me/activity) en roept
// setAnalyticsUser aan; bij de eerste bid/listing in de sessie upgraden de
// track-functies de status direct. Elke wijziging pusht een user_data_ready-
// event, en user_type/user_id gaan bovendien met elk event mee als DLV.
export type CumulativeUserType =
  | "guest"
  | "new"
  | "buyer"
  | "seller"
  | "buyer_seller";

let currentUserId: string | undefined;
let hasBids = false;
let hasListings = false;
let hasPurchases = false;
let hasApprovedListings = false;

function computeUserType(): CumulativeUserType {
  if (!currentUserId) return "guest";
  if (hasBids && hasListings) return "buyer_seller";
  if (hasBids) return "buyer";
  if (hasListings) return "seller";
  return "new";
}

/**
 * user_role volgens de analytics-briefing (A.3): buyer_only zodra ooit
 * geboden of gekocht, seller_only zodra ooit een goedgekeurde listing,
 * both bij beide, leeg ("") bij niet ingelogd of nog geen activiteit.
 */
function computeUserRole(): string {
  if (!currentUserId) return "";
  const buyerSide = hasBids || hasPurchases;
  const sellerSide = hasApprovedListings;
  if (buyerSide && sellerSide) return "both";
  if (buyerSide) return "buyer_only";
  if (sellerSide) return "seller_only";
  return "";
}

function announceUserType(): void {
  push({
    event: "user_data_ready",
    user_type: computeUserType(),
    user_role: computeUserRole(),
  });
}

export function setAnalyticsUser(
  userId: string | undefined,
  activity?: {
    hasBids: boolean;
    hasListings: boolean;
    hasPurchases?: boolean;
    hasApprovedListings?: boolean;
  },
): void {
  currentUserId = userId;
  hasBids = activity?.hasBids ?? false;
  hasListings = activity?.hasListings ?? false;
  hasPurchases = activity?.hasPurchases ?? false;
  hasApprovedListings = activity?.hasApprovedListings ?? false;
  announceUserType();
}

/** In-sessie-upgrade zodra de gebruiker voor het eerst biedt of aanbiedt. */
function markActivity(kind: "bid" | "listing"): void {
  if (!currentUserId) return;
  const before = computeUserType() + computeUserRole();
  if (kind === "bid") hasBids = true;
  else hasListings = true;
  if (computeUserType() + computeUserRole() !== before) announceUserType();
}

function push(obj: DataLayerObject): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // Reset het ecommerce-object vóór elke e-commerce push: GA4 raadt dit aan
  // zodat data van een vorig event niet "blijft plakken".
  if ("ecommerce" in obj) window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    user_type: computeUserType(),
    user_role: computeUserRole(),
    user_id: currentUserId,
    ...obj,
  });
}

/**
 * GA4 client_id uit de _ga-cookie (formaat "GA1.1.12345.67890" → "12345.67890").
 * Nodig voor server-side Measurement Protocol-events; de cookie wordt gezet
 * door de GA4-tag die via GTM laadt.
 */
export function getGaClientId(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return null;
  const parts = decodeURIComponent(match[1]).split(".");
  return parts.length >= 4 ? parts.slice(2).join(".") : null;
}

function num(v: unknown): number {
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
}

// ── Item-vorm voor e-commerce events ────────────────────────────────────────
export interface AnalyticsItem {
  item_id: string;
  item_name?: string;
  item_category?: string; // 'Retro' | 'Modern' | …
  item_brand?: string; // club
  price?: number;
  quantity?: number;
  item_variant?: string; // maat
  item_list_name?: string; // 'Veiling' | 'Vaste prijs'
}

function toItem(i: AnalyticsItem): DataLayerObject {
  return {
    item_id: i.item_id,
    item_name: i.item_name,
    item_category: i.item_category,
    item_brand: i.item_brand,
    price: i.price != null ? num(i.price) : undefined,
    quantity: i.quantity ?? 1,
    item_variant: i.item_variant,
    item_list_name: i.item_list_name,
  };
}

// ── SPA pageview (bij elke route-change) ────────────────────────────────────
// Onthoudt daarnaast via welke pagina de gebruiker op een veilingpagina
// belandde — dat wordt de discovery_source op bid_placed (briefing A.4).
let previousPagePath: string | null = null;
let auctionDiscoverySource = "direct_link";

const AUCTION_DETAIL_RE = /^\/shirt\/auction\//;

function discoverySourceFromPath(path: string | null): string {
  if (!path) return "direct_link";
  if (path === "/" || path.startsWith("/?")) return "homepage";
  if (path.startsWith("/catalog") && path.includes("type=ending-soon")) {
    return "ending_soon_page";
  }
  if (path.startsWith("/favorites")) return "favorites";
  if (path.startsWith("/catalog")) return "search";
  return "direct_link";
}

export function trackPageView(path: string, title: string): void {
  if (
    AUCTION_DETAIL_RE.test(path) &&
    !AUCTION_DETAIL_RE.test(previousPagePath ?? "")
  ) {
    auctionDiscoverySource = discoverySourceFromPath(previousPagePath);
  }
  previousPagePath = path;
  push({ event: "virtual_pageview", page_path: path, page_title: title });
}

/** Structureel shirt-type voor analytics, zodat we niet aan ApiShirt koppelen. */
export interface ShirtLike {
  id: string;
  club?: string | null;
  competition?: string | null;
  player?: string | null;
  brand?: string | null;
  size?: string | null;
  season?: string | null;
  price?: number | null;
  sales_type?: string | null;
  is_matchworn?: boolean | null;
}

export type AuctionType = "timed" | "buy-now";

export function auctionTypeFromSalesType(salesType?: string | null): AuctionType {
  return salesType === "auction" ? "timed" : "buy-now";
}

/**
 * listing_category: 'match-worn' | 'retro' | 'modern'. De backend kent geen
 * categorieveld; match-worn is expliciet, retro/modern leiden we af uit het
 * seizoen (RETRO_AGE_YEARS of ouder = retro).
 */
const RETRO_AGE_YEARS = 10;

export function listingCategoryFromShirt(s: {
  is_matchworn?: boolean | null;
  season?: string | null;
}): string {
  if (s.is_matchworn) return "match-worn";
  const year = parseInt(String(s.season ?? "").match(/(19|20)\d{2}/)?.[0] ?? "", 10);
  if (Number.isFinite(year) && new Date().getFullYear() - year >= RETRO_AGE_YEARS) {
    return "retro";
  }
  return "modern";
}

/** Bouwt een AnalyticsItem uit een shirt. */
export function itemFromShirt(s: ShirtLike): AnalyticsItem {
  const listName = s.sales_type === "auction" ? "Veiling" : "Vaste prijs";
  const name =
    [s.club || s.competition, s.season].filter(Boolean).join(" ") ||
    s.player ||
    s.brand ||
    s.id;
  return {
    item_id: s.id,
    item_name: name || undefined,
    item_category: listingCategoryFromShirt(s),
    item_brand: s.club || undefined,
    price: s.price ?? undefined,
    item_variant: s.size || undefined,
    item_list_name: listName,
  };
}

// ── view_item — shirt-detailpagina ──────────────────────────────────────────
export function trackViewItem(shirt: ShirtLike): void {
  const item = itemFromShirt(shirt);
  push({
    event: "view_item",
    // Platte kopieën voor de Meta/TikTok-tags in GTM: TikTok ViewContent
    // vereist value + content_id (= listing_id) als losse DLV's. De briefing
    // (item 4) noemt de keys listing_value/listing_currency — beide vormen
    // worden gepusht zodat de GTM-tags elk van de twee kunnen lezen.
    value: num(item.price),
    currency: CURRENCY,
    listing_value: num(item.price),
    listing_currency: CURRENCY,
    listing_id: shirt.id,
    listing_category: item.item_category,
    auction_type: auctionTypeFromSalesType(shirt.sales_type),
    ecommerce: { currency: CURRENCY, value: num(item.price), items: [toItem(item)] },
  });
}

// ── search ──────────────────────────────────────────────────────────────────
export function trackSearch(searchTerm: string, resultsCount: number): void {
  push({ event: "search", search_term: searchTerm, results_count: resultsCount });
}

// ── add_to_cart / add_to_wishlist ────────────────────────────────────────────
export function trackAddToCart(item: AnalyticsItem): void {
  push({
    event: "add_to_cart",
    ecommerce: { currency: CURRENCY, value: num(item.price), items: [toItem(item)] },
  });
}

export function trackAddToWishlist(item: AnalyticsItem): void {
  push({
    event: "add_to_wishlist",
    ecommerce: { currency: CURRENCY, value: num(item.price), items: [toItem(item)] },
  });
}

// ── begin_checkout ────────────────────────────────────────────────────────────
export function trackBeginCheckout(value: number, items: AnalyticsItem[]): void {
  push({
    event: "begin_checkout",
    ecommerce: { currency: CURRENCY, value: num(value), items: items.map(toItem) },
  });
}

// ── purchase (KRITISCH — dedupe op transaction_id bij de caller!) ─────────────
export interface PurchaseInfo {
  transactionId: string;
  value: number;
  items: AnalyticsItem[];
  tax?: number;
  shipping?: number;
  coupon?: string;
  platformFee?: number;
  sellerPayout?: number;
  auctionType?: AuctionType;
  listingCategory?: string;
}

export function trackPurchase(p: PurchaseInfo): void {
  push({
    event: "purchase",
    // Platte kopieën van de transactieparameters: de Meta Purchase-tag in GTM
    // leest value/currency/transaction_id (eventID) als losse DLV's.
    transaction_id: p.transactionId,
    value: num(p.value),
    currency: CURRENCY,
    listing_id: p.items[0]?.item_id,
    listing_category: p.listingCategory ?? p.items[0]?.item_category,
    auction_type: p.auctionType,
    ecommerce: {
      transaction_id: p.transactionId,
      value: num(p.value),
      tax: p.tax != null ? num(p.tax) : undefined,
      shipping: p.shipping != null ? num(p.shipping) : undefined,
      currency: CURRENCY,
      coupon: p.coupon || "",
      items: p.items.map(toItem),
    },
    platform_fee: p.platformFee != null ? num(p.platformFee) : undefined,
    seller_payout: p.sellerPayout != null ? num(p.sellerPayout) : undefined,
  });
}

// ── complete_registration ─────────────────────────────────────────────────────
export type RegistrationMethod = "email" | "google" | "facebook";

export function trackCompleteRegistration(
  method: RegistrationMethod,
  userId: string,
): void {
  push({
    event: "complete_registration",
    // Bewust géén user_type bij registratie: FSM kent één generiek accounttype;
    // koper/verkoper wordt later afgeleid uit gedrag (eerste listing/bod) —
    // zie de Priority 1-briefing. Expliciet undefined om de globale default
    // van push() op dit event te onderdrukken.
    user_type: undefined,
    registration_method: method,
    user_id: userId,
    value: 0,
    currency: CURRENCY,
  });
}

// ── listing_created (KRITISCH — Supply KPI) ───────────────────────────────────
export interface ListingInfo {
  listingId: string;
  listingType: "fixed_price" | "auction" | "best_offer";
  listingPrice: number;
  shirtClub?: string;
  shirtSeason?: string;
  shirtCondition?: string;
  sellerId?: string;
  isMatchworn?: boolean;
}

export function trackListingCreated(l: ListingInfo): void {
  markActivity("listing");
  push({
    event: "listing_created",
    listing_id: l.listingId,
    listing_type: l.listingType,
    listing_category: listingCategoryFromShirt({
      is_matchworn: l.isMatchworn,
      season: l.shirtSeason,
    }),
    auction_type: auctionTypeFromSalesType(l.listingType),
    listing_price: num(l.listingPrice),
    shirt_club: l.shirtClub,
    shirt_season: l.shirtSeason,
    shirt_condition: l.shirtCondition,
    seller_id: l.sellerId,
    currency: CURRENCY,
  });
}

// ── bid_placed / auction_won ──────────────────────────────────────────────────
export interface BidInfo {
  auctionId: string;
  /** Door de server bevestigd bedrag. */
  bidAmount: number;
  shirt?: ShirtLike | null;
  /** Fallback wanneer het shirt (nog) niet geladen is. */
  shirtId?: string;
}

export function trackBidPlaced(b: BidInfo): void {
  markActivity("bid");
  const listingId = b.shirt?.id ?? b.shirtId ?? b.auctionId;
  push({
    event: "bid_placed",
    // Parameters voor de Meta bid_placed-tag in GTM (vervangt de foutieve
    // SubscribedButtonClick-koppeling): bid_value/listing_id/listing_category.
    discovery_source: auctionDiscoverySource,
    bid_value: num(b.bidAmount),
    bid_currency: CURRENCY,
    listing_id: listingId,
    listing_category: b.shirt ? listingCategoryFromShirt(b.shirt) : undefined,
    auction_type: "timed",
    // Bestaande GA4-parameters — laten staan, tags verwijzen ernaar.
    auction_id: b.auctionId,
    bid_amount: num(b.bidAmount),
    shirt_id: listingId,
    shirt_name: b.shirt ? itemFromShirt(b.shirt).item_name : undefined,
    currency: CURRENCY,
  });
}

export interface AuctionWonInfo {
  auctionId: string;
  /** Order-id van de transactie; caller valt terug op auctionId zolang er nog geen order is. */
  transactionId: string;
  /** Winnend bod. */
  value: number;
  shirt?: ShirtLike | null;
  shirtId?: string;
}

export function trackAuctionWon(w: AuctionWonInfo): void {
  const listingId = w.shirt?.id ?? w.shirtId ?? w.auctionId;
  push({
    event: "auction_won",
    // GTM stuurt dit door als standaard Meta Purchase-event; transaction_id
    // wordt daar de eventID zodat pixel + Conversions API dedupliceren.
    transaction_id: w.transactionId,
    value: num(w.value),
    currency: CURRENCY,
    listing_id: listingId,
    listing_category: w.shirt ? listingCategoryFromShirt(w.shirt) : undefined,
    auction_type: "timed",
    // Bestaande parameters — laten staan, tags verwijzen ernaar.
    auction_id: w.auctionId,
    shirt_id: listingId,
  });
}

// ── listing_submitted — seller dient listing in (briefing A.2) ────────────────
export interface ListingSubmittedInfo {
  listingId: string;
  listingType: "auction" | "fixed";
  shirtClub?: string;
  /** Alleen bij fixed. */
  listingPrice?: number;
  /** Alleen bij auction (minimum-/startprijs). */
  auctionMinPrice?: number;
  /** Alleen bij auction; afgeleid uit de gekozen einddatum. */
  auctionDurationDays?: number;
  photoCount: number;
}

export function trackListingSubmitted(l: ListingSubmittedInfo): void {
  push({
    event: "listing_submitted",
    listing_id: l.listingId,
    listing_type: l.listingType,
    shirt_club: l.shirtClub,
    listing_price: l.listingType === "fixed" ? num(l.listingPrice) : undefined,
    auction_min_price:
      l.listingType === "auction" ? num(l.auctionMinPrice) : undefined,
    auction_duration_days:
      l.listingType === "auction" ? l.auctionDurationDays : undefined,
    photo_count: l.photoCount,
  });
}

// ── listing_approved — moderator keurt listing goed (briefing A.1) ────────────
// Let op: relist_count is altijd 0 — FSM heeft geen relist-teller; herlanceren
// maakt een nieuwe veiling op hetzelfde shirt zonder teller in het datamodel.
export function trackListingApproved(listingId: string, photoCount: number): void {
  push({
    event: "listing_approved",
    listing_id: listingId,
    photo_count: photoCount,
    relist_count: 0,
  });
}

// ── dispute_opened — koper meldt probleem (briefing A.5) ──────────────────────
// De UI kent niet exact de reason-lijst uit de briefing; bekende waarden worden
// gemapt, overige (fraud/other) gaan ongewijzigd mee.
const DISPUTE_REASON_MAP: Record<string, string> = {
  not_as_described: "item_not_as_described",
  not_arrived: "item_not_received",
  damaged: "damaged",
  wrong_item: "wrong_item",
};

export function trackDisputeOpened(d: {
  transactionId: string;
  listingId?: string;
  reason: string;
}): void {
  push({
    event: "dispute_opened",
    transaction_id: d.transactionId,
    listing_id: d.listingId,
    dispute_reason: DISPUTE_REASON_MAP[d.reason] ?? d.reason,
  });
}

// ── message_sent — bericht naar verkoper (koopintentie pre-checkout) ──────────
export function trackMessageSent(meta?: {
  chat_id?: string;
  shirt_id?: string;
  receiver_id?: string;
}): void {
  push({ event: "message_sent", ...meta });
}
